import { Cancel, Image } from '@mui/icons-material';
import { Box, FormHelperText, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  ButtonVariants,
  DatePicker,
  InputLabel,
  LabelCheckbox,
  ModalContentContainer,
  SingleSelectDropdown,
  TextField,
} from '@/components';
import { Modals, TrackerMessages, UsaStates } from '@/constants';
import { AnalyticsService, EVENT_CONSTANTS, EVENTS } from '@/services';
import {
  selectActiveModalParams,
  selectUserCompliance,
  uiActions,
  useCreateCustomCertificateMutation,
  useFetchFieldsOfStudyQuery,
  useUpdateCustomCertificateMutation,
} from '@/store';
import {
  Certificate,
  CPDDeliveryMethodOptions,
  CPDProviderOptions,
  CPEDeliveryMethodOptions,
  CPEProviderOptions,
  CustomCertificateInput,
} from '@/types';
import { displaySize, SnackbarUtils } from '@/utils';

import { CERT_FILE_TYPES, validateCustomCertificateValues } from './utils';

const nextYear = new Date().getFullYear() + 1;

export const TrackerAddEditModalContent = () => {
  const dispatch = useDispatch();

  const params = useSelector(selectActiveModalParams) || {};
  const credit = params.credit as Certificate;

  const complianceType = useSelector(selectUserCompliance);

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [values, setValues] = useState<CustomCertificateInput>(() => {
    if (!credit)
      return {
        title: '',
        credit: '',
        dateCompleted: new Date().toString(),
        provider: CPDProviderOptions[0],
        otherProvider: undefined,
        description: '',
        isEthics: false,
        file: undefined,
        originalFile: undefined,
        ...(complianceType === 'CPE'
          ? {
              complianceType: 'CPE' as const,
              deliveryMethod: CPEDeliveryMethodOptions[0],
              location: 'Please Select',
              fieldOfStudy: [],
            }
          : {
              complianceType: 'CPD' as const,
              deliveryMethod: CPDDeliveryMethodOptions[0],
            }),
      };
    const combinedProviders = [...CPDProviderOptions, ...CPEProviderOptions];
    return {
      title: credit.title,
      credit: `${credit.credit}`,
      dateCompleted: credit.dateCompleted,
      provider: credit.provider
        ? combinedProviders.includes(
            credit.provider as (typeof combinedProviders)[number],
          )
          ? (credit.provider as (typeof combinedProviders)[number])
          : 'Other'
        : combinedProviders[0],
      otherProvider:
        credit.provider &&
        !combinedProviders.includes(
          credit.provider as (typeof combinedProviders)[number],
        )
          ? credit.provider
          : undefined,
      description: credit.description,
      isEthics: !!credit.ethicsCredit,
      file: undefined,
      originalFile: credit.fileName
        ? {
            name: credit.fileName!,
            size: credit.fileSize!,
            uri: credit.fileUri!,
          }
        : undefined,
      ...(complianceType === 'CPE'
        ? {
            complianceType: 'CPE' as const,
            deliveryMethod: (credit.deliveryMethod ||
              CPEDeliveryMethodOptions[0]) as (typeof CPEDeliveryMethodOptions)[number],
            location: credit.location || '',
            fieldOfStudy:
              credit.fieldsOfStudy?.map(fos => ({
                fieldOfStudyId: fos.fieldOfStudyId,
                name: fos.name!,
              })) || [],
          }
        : {
            complianceType: 'CPD' as const,
            deliveryMethod: (credit.deliveryMethod ||
              CPDDeliveryMethodOptions[0]) as (typeof CPDDeliveryMethodOptions)[number],
          }),
    };
  });
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const [createCustomCertificate, { isLoading: isCreateLoading }] =
    useCreateCustomCertificateMutation();
  const [updateCustomCertificate, { isLoading: isUpdateLoading }] =
    useUpdateCustomCertificateMutation();
  const { data: fieldsOfStudy } = useFetchFieldsOfStudyQuery();

  const isLoading = isCreateLoading || isUpdateLoading;

  useEffect(() => {
    // Validate form on every change after the first submit attempt
    if (!attemptedSubmit) return;
    const newErrors = validateCustomCertificateValues(values);
    setErrors(newErrors);
  }, [values, attemptedSubmit]);

  const onSubmitCreate = async () => {
    const newErrors = validateCustomCertificateValues(values);
    if (Object.keys(newErrors).length) {
      setAttemptedSubmit(true);
      return;
    }
    const formData = new FormData();
    formData.set('title', values.title);
    if (values.description) {
      formData.set('description', values.description);
    }
    formData.set('isNasba', 'false');
    formData.set('hoursOrCredit', values.credit);
    formData.set('dateCompleted', values.dateCompleted);
    formData.set(
      'deliveryMethod',
      values.deliveryMethod === 'Please Select' ? '' : values.deliveryMethod,
    );
    formData.set(
      'provider',
      values.provider === 'Please Select'
        ? ''
        : values.provider === 'Other'
          ? values.otherProvider!
          : values.provider,
    );
    if (values.isEthics) {
      formData.set('ethicHours', values.credit);
    }
    if (values.file) {
      formData.append('file', values.file);
    }
    if (values.complianceType === 'CPE') {
      formData.set('location', values.location);
      formData.set('fieldOfStudy', values.fieldOfStudy[0].fieldOfStudyId);
    }
    AnalyticsService.logEvent(
      EVENTS.TRACKER_EVENTS.CREATE_CUSTOM_CERTIFICATE.INTENT,
      { formData: Object.fromEntries(formData.entries()) },
    );
    const { error } = await createCustomCertificate(formData);
    if (error) {
      SnackbarUtils.error(TrackerMessages.CREATE_CUSTOM_CPD_ERROR);
      AnalyticsService.logEvent(
        EVENTS.TRACKER_EVENTS.CREATE_CUSTOM_CERTIFICATE.FAIL,
        { error: JSON.stringify(error) },
      );
      return;
    }
    SnackbarUtils.success(TrackerMessages.CREATE_CUSTOM_CPD_SUCCESS);
    AnalyticsService.logEvent(
      EVENTS.TRACKER_EVENTS.CREATE_CUSTOM_CERTIFICATE.SUCCESS,
    );
    onClose();
  };

  const onSubmitUpdate = async () => {
    const newErrors = validateCustomCertificateValues(values);
    if (Object.keys(newErrors).length) {
      setAttemptedSubmit(true);
      return;
    }
    AnalyticsService.logEvent(
      EVENTS.TRACKER_EVENTS.UPDATE_CUSTOM_CERTIFICATE.INTENT,
      {
        certificateId: credit.certificateId,
        certificateData: values,
      },
    );
    const { error } = await updateCustomCertificate({
      certificateId: credit.certificateId,
      certificateData: values,
    });
    if (error) {
      SnackbarUtils.error(TrackerMessages.UPDATE_CUSTOM_CPD_ERROR);
      AnalyticsService.logEvent(
        EVENTS.TRACKER_EVENTS.UPDATE_CUSTOM_CERTIFICATE.FAIL,
        { error: JSON.stringify(error) },
      );
      return;
    }
    SnackbarUtils.success(TrackerMessages.UPDATE_CUSTOM_CPD_SUCCESS);
    AnalyticsService.logEvent(
      EVENTS.TRACKER_EVENTS.UPDATE_CUSTOM_CERTIFICATE.SUCCESS,
    );
    onClose();
  };

  const onClose = () => {
    dispatch(uiActions.closeActiveModal());
  };

  const onDelete = () => {
    dispatch(uiActions.closeActiveModal());
    dispatch(
      uiActions.setActiveModal({
        name: Modals.TRACKER_DELETE,
        params: { credit },
      }),
    );
  };

  const isEditMode = !!credit;

  const file = values.file ?? values.originalFile;
  const certUnits = complianceType === 'CPD' ? 'Hours' : 'Credits';

  return (
    <Box sx={{ maxWidth: 640 }}>
      <ModalContentContainer onModalClose={onClose} stretch>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={styles.titleContainer}>
            <Typography variant="h3">
              {`${isEditMode ? 'Edit' : 'Add'} External ${certUnits}`}
            </Typography>
          </Box>
          <Box>
            <TextField
              name="source"
              label="Course/Seminar Title"
              type="text"
              placeholder="e.g. Annual Tax Update"
              required
              fullWidth
              value={values.title || ''}
              error={!!errors.title}
              disabled={isLoading}
              onChange={event =>
                setValues({ ...values, title: event.target.value })
              }
            />
            {!!errors.title && (
              <FormHelperText variant="filled" error>
                {errors.title}
              </FormHelperText>
            )}
          </Box>
          <Box sx={styles.rowContainer}>
            <Box sx={{ flex: 1, width: '100%' }}>
              <TextField
                name="hours"
                label={complianceType === 'CPD' ? 'CPD Hours' : 'CPE Credits'}
                type="number"
                placeholder="0.0"
                required
                fullWidth
                value={values.credit || ''}
                error={!!errors.credit}
                disabled={isLoading}
                onChange={event =>
                  setValues({ ...values, credit: event.target.value })
                }
              />
            </Box>
            {values.complianceType === 'CPD' && (
              <>
                <Box sx={styles.dayPickerContainer}>
                  <InputLabel id="date" label="Date *" />
                  <DatePicker
                    fullWidth
                    minDate={new Date(2012, 8, 1)}
                    maxDate={new Date(nextYear, 11, 31)}
                    disabled={isLoading}
                    value={new Date(values.dateCompleted)}
                    onChange={date =>
                      setValues({
                        ...values,
                        dateCompleted: date?.toString() || '',
                      })
                    }
                    allowClear={false}
                    allowTextInput={false}
                    referrer={{
                      page: EVENT_CONSTANTS.PAGE.TRACKER,
                      modalName: Modals.TRACKER_ADD_EDIT,
                    }}
                    error={!!errors.dateCompleted}
                  />
                  {!!errors.dateCompleted && (
                    <FormHelperText variant="filled" error>
                      {errors.dateCompleted}
                    </FormHelperText>
                  )}
                </Box>
                <Box sx={styles.inputContainer}>
                  <Box sx={{ height: { xs: 0, md: 22 } }} />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                    }}>
                    <LabelCheckbox
                      title="Qualified as Ethics"
                      checked={values.isEthics}
                      onCheck={checked =>
                        setValues({ ...values, isEthics: checked })
                      }
                    />
                  </Box>
                </Box>
              </>
            )}
            {values.complianceType === 'CPE' && (
              <Box
                sx={[
                  styles.inputContainer,
                  {
                    flex: 1,
                  },
                ]}>
                <InputLabel id="fieldOfStudy" label="Field of Study *" />
                <SingleSelectDropdown
                  selectedOption={
                    values.fieldOfStudy?.[0]?.fieldOfStudyId || ''
                  }
                  options={Object.entries(fieldsOfStudy || {})
                    .sort(([_a, fosA], [_b, fosB]) => fosA.localeCompare(fosB))
                    .reduce(
                      (acc, [id, fos]) => {
                        acc[id] = { text: fos };
                        return acc;
                      },
                      { '': { text: 'Please Select' } } as Record<
                        string,
                        { text: string }
                      >,
                    )}
                  title=""
                  isFormInput={true}
                  error={!!errors.fieldOfStudy}
                  onSave={option =>
                    setValues({
                      ...values,
                      fieldOfStudy: option
                        ? [
                            {
                              fieldOfStudyId: option,
                              name: Object.entries(fieldsOfStudy || {}).find(
                                ([id]) => id === option,
                              )![1],
                            },
                          ]
                        : [],
                    })
                  }
                />
              </Box>
            )}
          </Box>
          {values.complianceType === 'CPE' && (
            <Box sx={styles.rowContainer}>
              <Box sx={styles.inputContainer}>
                <InputLabel id="location" label="State" />
                <SingleSelectDropdown
                  selectedOption={values.location}
                  options={Object.entries(UsaStates).reduce(
                    (acc, [code, name]) => {
                      acc[code] = { text: name };
                      return acc;
                    },
                    { 'Please Select': { text: 'Please Select' } } as Record<
                      string,
                      { text: string }
                    >,
                  )}
                  title=""
                  isFormInput={true}
                  error={!!errors.location}
                  onSave={option =>
                    setValues({
                      ...values,
                      location: option,
                    })
                  }
                />
              </Box>
              <Box sx={styles.dayPickerContainer}>
                <InputLabel id="date" label="Date *" />
                <DatePicker
                  minDate={new Date(2012, 8, 1)}
                  maxDate={new Date(nextYear, 11, 31)}
                  disabled={isLoading}
                  fullWidth
                  value={new Date(values.dateCompleted)}
                  onChange={date =>
                    setValues({
                      ...values,
                      dateCompleted: date?.toString() || '',
                    })
                  }
                  allowClear={false}
                  allowTextInput={false}
                  referrer={{
                    page: EVENT_CONSTANTS.PAGE.TRACKER,
                    modalName: Modals.TRACKER_ADD_EDIT,
                  }}
                  error={!!errors.dateCompleted}
                />
                {!!errors.dateCompleted && (
                  <FormHelperText variant="filled" error>
                    {errors.dateCompleted}
                  </FormHelperText>
                )}
              </Box>
            </Box>
          )}
          <Box sx={styles.rowContainer}>
            <Box sx={styles.inputContainer}>
              <InputLabel
                id="deliveryMethod"
                label={`Delivery Method${complianceType === 'CPE' ? ' *' : ''}`}
              />
              <SingleSelectDropdown
                isFormInput={true}
                selectedOption={
                  values.deliveryMethod || CPDDeliveryMethodOptions[0]
                }
                options={(complianceType === 'CPD'
                  ? CPDDeliveryMethodOptions
                  : CPEDeliveryMethodOptions
                ).reduce(
                  (acc, method) => {
                    acc[method] = { text: method };
                    return acc;
                  },
                  {} as Record<string, { text: string }>,
                )}
                title=""
                onSave={option => {
                  setValues({
                    ...values,
                    deliveryMethod: option as any,
                  });
                }}
                error={!!errors.deliveryMethod}
              />
            </Box>
            <Box sx={styles.inputContainer}>
              <InputLabel
                id="provider"
                label={`Provider${complianceType === 'CPE' ? ' *' : ''}`}
              />
              <SingleSelectDropdown
                selectedOption={values.provider || CPDProviderOptions[0]}
                options={(complianceType === 'CPD'
                  ? CPDProviderOptions
                  : CPEProviderOptions
                ).reduce(
                  (acc, provider) => {
                    acc[provider] = { text: provider };
                    return acc;
                  },
                  {} as Record<string, { text: string }>,
                )}
                title=""
                fullSize
                isFormInput={true}
                onSave={option =>
                  setValues({
                    ...values,
                    provider: option,
                  })
                }
                error={!!errors.provider}
              />
            </Box>
          </Box>
          {values.provider === 'Other' && (
            <Box>
              <TextField
                name="otherProvider"
                label="Specify Provider Name"
                type="text"
                placeholder="Enter provider name"
                fullWidth
                value={values.otherProvider || ''}
                error={!!errors.otherProvider}
                helperText={errors.otherProvider}
                disabled={isLoading}
                onChange={event =>
                  setValues({ ...values, otherProvider: event.target.value })
                }
              />
            </Box>
          )}
          <Box>
            <TextField
              name="description"
              label="Course Description"
              type="text"
              placeholder="Briefly describe the course content"
              multiline
              rows={4}
              fullWidth
              value={values.description || ''}
              helperText={`${values.description?.length || 0}/2000 characters`}
              error={!!errors.description}
              disabled={isLoading}
              onChange={event =>
                setValues({ ...values, description: event.target.value })
              }
            />
            {!!errors.description && (
              <FormHelperText variant="filled" error>
                {errors.description}
              </FormHelperText>
            )}
          </Box>
          <Box>
            <Typography variant="body3">
              You can upload a photo of a certificate, attendance sheet or any
              other document that verifies you completed your CPD hours.
            </Typography>
            <Box sx={styles.uploadContainer}>
              <Button
                sx={{ mr: 2 }}
                variant={ButtonVariants.PRIMARY}
                fullWidth={false}
                skinny={false}
                startIcon={<Image />}
                label={
                  values.originalFile
                    ? 'Uploaded file'
                    : 'Upload file (optional)'
                }
                disabled={isLoading || !!credit}
                fileUploadProps={{
                  onChange: event => {
                    const { target } = event;
                    if (!target.files || target.files.length === 0) return;
                    setValues({ ...values, file: target.files[0] });
                    target.value = '';
                  },
                  accept: CERT_FILE_TYPES.join(','),
                }}
              />
              {!!file && (
                <>
                  <Typography sx={styles.fileName}>{file.name}</Typography>
                  <Typography variant="body3SemiBold" sx={styles.fileInfoSize}>
                    {displaySize(file.size)}
                  </Typography>
                  {!values.originalFile && (
                    <IconButton
                      size="small"
                      disabled={isLoading}
                      onClick={() =>
                        setValues({
                          ...values,
                          file: undefined,
                          originalFile: undefined,
                        })
                      }
                      aria-label="Remove file">
                      <Cancel sx={styles.cancelIcon} />
                    </IconButton>
                  )}
                </>
              )}
              {!file && (
                <Typography variant="body3" sx={styles.fileInfoDescription}>
                  No file selected yet (Max file size 10MB)
                </Typography>
              )}
            </Box>
            {!!errors.file && (
              <FormHelperText error>{errors.file}</FormHelperText>
            )}
          </Box>
          <Box>
            <Box sx={styles.buttonContainer}>
              {isEditMode ? (
                <>
                  <Box sx={{ mr: 2 }}>
                    <Button
                      label="Delete"
                      onClick={onDelete}
                      variant="gradientPrimary"
                      fullWidth={false}
                      disabled={isLoading}
                    />
                  </Box>
                  <Button
                    label="Save"
                    onClick={onSubmitUpdate}
                    variant="primary"
                    fullWidth={false}
                    disabled={isLoading}
                  />
                </>
              ) : (
                <Button
                  label="Submit"
                  onClick={onSubmitCreate}
                  variant="primary"
                  skinny={false}
                  fullWidth={false}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Box>
        </Box>
      </ModalContentContainer>
    </Box>
  );
};

const styles = {
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowContainer: {
    display: 'flex',
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: 'center',
    gap: 2,
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
  },
  dayPickerContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
  },
  uploadContainer: {
    display: 'flex',
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
    justifyContent: 'flex-start',
    mt: 2,
    gap: 1,
  },
  buttonContainer: {
    textAlign: 'right',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  fileInfo: {
    color: 'blueBlack',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  fileName: {
    display: 'inline-block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  fileInfoSize: {
    color: 'grey175',
    mx: 1,
    textWrap: 'nowrap',
  },
  fileInfoDescription: {
    color: 'grey175',
  },
  cancelIcon: {
    color: 'secondary.main',
    fontSize: 24,
  },
};
